import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { apiFetch } from "../config/api";

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const navigate = useNavigate();

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  async function submit() {
    if (!newPassword.trim()) return setError("Please enter a new password.");
    if (newPassword.length < 6) return setError("Password must be at least 6 characters.");
    if (newPassword !== confirmPassword) return setError("Passwords do not match.");
    setError("");
    setLoading(true);
    try {
      const res = await apiFetch("/api/auth/reset-password-by-token", {
        method: "POST",
        body: JSON.stringify({ token, newPassword }),
      });
      if (!res.ok) {
        const msg = await res.text();
        throw new Error(msg || "Reset failed.");
      }
      setSuccess(true);
      setTimeout(() => navigate("/login"), 3000);
    } catch (e) {
      setError(e?.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  if (!token) {
    return (
      <div style={styles.root}>
        <div style={styles.dotGrid} />
        <div style={styles.card}>
          <div style={{ color: "#991b1b", fontSize: 15, marginBottom: 16 }}>
            Invalid reset link. No token found.
          </div>
          <Link to="/login" style={{ fontSize: 13, color: "#0b72b9", textDecoration: "none", fontWeight: 600 }}>
            ← Back to Sign In
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.root}>
      <div style={styles.dotGrid} />
      <div style={styles.glowTop} />

      <div style={styles.card}>
        <div style={styles.logoRow}>
          <img src="/logo.svg" alt="Tackl" style={{ width: 32, height: 32, objectFit: "contain" }} />
          <div>
            <div style={styles.wordmark}>Tackl</div>
            <div style={styles.wordmarkSub}>TACKL</div>
          </div>
        </div>

        {success ? (
          <>
            <div style={{ fontSize: 22, fontWeight: 700, color: "#111827", marginBottom: 10 }}>Password reset!</div>
            <p style={{ fontSize: 14, color: "#697386", lineHeight: 1.6 }}>
              Your password has been updated. Redirecting you to sign in…
            </p>
            <div style={{ marginTop: 20 }}>
              <Link to="/login" style={{ fontSize: 13, color: "#0b72b9", textDecoration: "none", fontWeight: 600 }}>
                Go to Sign In →
              </Link>
            </div>
          </>
        ) : (
          <>
            <div style={{ marginBottom: 24 }}>
              <h1 style={styles.heading}>Set a new password</h1>
              <p style={styles.subheading}>Choose a strong password with at least 6 characters.</p>
            </div>

            {error && <div style={styles.errorBox}>{error}</div>}

            <div style={styles.form}>
              <div style={styles.field}>
                <label style={styles.label}>NEW PASSWORD</label>
                <input
                  type="password"
                  placeholder="••••••••"
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && submit()}
                  style={styles.input}
                />
              </div>

              <div style={styles.field}>
                <label style={styles.label}>CONFIRM PASSWORD</label>
                <input
                  type="password"
                  placeholder="••••••••"
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && submit()}
                  style={styles.input}
                />
              </div>

              <button
                onClick={submit}
                disabled={loading}
                style={{ ...styles.submitBtn, opacity: loading ? 0.7 : 1, cursor: loading ? "not-allowed" : "pointer" }}
              >
                {loading ? "Saving…" : "Reset Password →"}
              </button>
            </div>
          </>
        )}

        <div style={{ marginTop: 24, textAlign: "center" }}>
          <Link to="/login" style={{ fontSize: 13, color: "#0b72b9", textDecoration: "none", fontWeight: 600 }}>
            ← Back to Sign In
          </Link>
        </div>
      </div>

      <p style={styles.tagline}>Tackl</p>
    </div>
  );
}

const styles = {
  root: {
    minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center",
    justifyContent: "center", background: "#f5f6f9", position: "relative",
    overflow: "hidden", padding: "40px 20px",
    fontFamily: "'Segoe UI', 'Plus Jakarta Sans', system-ui, sans-serif",
  },
  dotGrid: {
    position: "absolute", inset: 0,
    backgroundImage: "radial-gradient(circle, rgba(17,24,39,0.045) 1px, transparent 1px)",
    backgroundSize: "28px 28px", pointerEvents: "none",
  },
  glowTop: {
    display: "none",
    position: "absolute", top: "-10%", left: "50%", transform: "translateX(-50%)",
    width: 600, height: 400, borderRadius: "50%",
    background: "radial-gradient(ellipse, rgba(8,145,178,0.18) 0%, transparent 70%)",
    pointerEvents: "none",
  },
  card: {
    position: "relative", width: "100%", maxWidth: 420,
    background: "#fff", border: "1px solid #dfe3ea",
    borderRadius: 12, padding: "34px 34px 30px",
    boxShadow: "0 12px 36px rgba(16,24,40,0.10)",
  },
  logoRow: { display: "flex", alignItems: "center", gap: 12, marginBottom: 28 },
  wordmark: { fontSize: 16, fontWeight: 700, color: "#111827", letterSpacing: 0, lineHeight: 1.2 },
  wordmarkSub: { fontSize: 10, fontWeight: 600, color: "#8a94a6", letterSpacing: "0.12em", marginTop: 2 },
  heading: { fontSize: 22, fontWeight: 700, color: "#111827", letterSpacing: 0, lineHeight: 1.25, marginBottom: 6 },
  subheading: { fontSize: 13, color: "#697386", lineHeight: 1.5 },
  errorBox: {
    padding: "10px 14px", borderRadius: 10, background: "var(--danger-soft)",
    border: "1px solid var(--danger-border)", color: "#991b1b", fontSize: 13, marginBottom: 14,
  },
  form: { display: "flex", flexDirection: "column", gap: 14 },
  field: { display: "flex", flexDirection: "column", gap: 6 },
  label: { fontSize: 11, fontWeight: 700, letterSpacing: "0.10em", color: "#697386" },
  input: {
    background: "#f8fafc", border: "1px solid #d5dbe6",
    color: "#111827", borderRadius: 8, padding: "11px 14px", fontSize: 14,
    outline: "none", width: "100%", fontFamily: "'Segoe UI', 'Plus Jakarta Sans', system-ui, sans-serif",
  },
  submitBtn: {
    marginTop: 4, width: "100%", padding: "12px", fontSize: 14, fontWeight: 600,
    background: "#0b72b9", color: "white",
    border: "none", borderRadius: 8, boxShadow: "0 3px 10px rgba(11,114,185,0.22)",
    fontFamily: "'Segoe UI', 'Plus Jakarta Sans', system-ui, sans-serif",
  },
  tagline: { marginTop: 28, fontSize: 12, color: "#8a94a6", letterSpacing: "0.02em" },
};
